import { useCallback, useState } from 'react';

import type {
  DocumentDraft,
  DocumentDraftContent,
  DocumentDraftSeed,
  DocumentDraftStore,
} from '@/entities/document-draft';
import type { DocumentEditorLoadState, DocumentEditorSaveState } from './documentEditorTypes';
import { useDocumentDraftLoader } from './useDocumentDraftLoader';
import { useDocumentDraftPersistence } from './useDocumentDraftPersistence';
import { useDocumentDraftState } from './useDocumentDraftState';

export type { DocumentEditorLoadState, DocumentEditorSaveState } from './documentEditorTypes';

interface UseDocumentDraftProps {
  patientId: string;
  draftId?: string;
  seed?: DocumentDraftSeed;
  store: DocumentDraftStore;
}

export interface UseDocumentDraftResult {
  draft: DocumentDraft | null;
  loadState: DocumentEditorLoadState;
  saveState: DocumentEditorSaveState;
  isNewDraft: boolean;
  updateDraft(patch: Partial<DocumentDraftContent>): void;
  updateDocumentMarkdown(documentMarkdown: string): void;
}

export function useDocumentDraft({
  patientId,
  draftId,
  seed,
  store,
}: UseDocumentDraftProps): UseDocumentDraftResult {
  const [saveState, setSaveState] = useState<DocumentEditorSaveState>('idle');
  const loader = useDocumentDraftLoader({ patientId, draftId, seed, store });
  const markEditing = useCallback(() => setSaveState('saving'), []);
  const draftState = useDocumentDraftState({
    loadedDraft: loader.loadedDraft,
    currentModifier: loader.currentModifier,
    onEdit: markEditing,
  });

  useDocumentDraftPersistence({
    patientId,
    store,
    loadKey: loader.loadKey,
    isNewDraft: loader.isNewDraft,
    hasLoadError: loader.hasLoadError,
    requiredDataLoading: loader.requiredDataLoading,
    loadedDraft: loader.loadedDraft,
    draft: draftState.draft,
    draftOverride: draftState.draftOverride,
    draftRef: draftState.draftRef,
    setSaveState,
  });

  return {
    draft: draftState.draft,
    loadState: loader.loadState,
    saveState,
    isNewDraft: loader.isNewDraft,
    updateDraft: draftState.updateDraft,
    updateDocumentMarkdown: draftState.updateDocumentMarkdown,
  };
}
